/*
 * The Tasks screen's state.
 *
 * `blockedBy` is the derivation the frozen frame leads with. A task's `dependsOn` is a list of ids;
 * what an organizer needs is the list of *unfinished* ones, because a dependency that is already
 * done blocks nothing. The frame's "ติดขัด" column is exactly that set being non-empty — it is not
 * a status anyone sets, and treating it as one would let a board show a task as blocked after the
 * thing blocking it was finished.
 *
 * Every write carries `version`. The server refuses a stale one, which is the whole point on a
 * board several people edit from a venue at once, and a refusal is surfaced rather than retried.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, opsApi } from '../../../api';
import type { OpsDepartmentDto, OpsStaffSummaryDto, OpsTaskDto, OpsTaskInput, OpsTaskStatus } from '../../../api';

export function blockersOf(tasks: readonly OpsTaskDto[]): Map<string, OpsTaskDto[]> {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const out = new Map<string, OpsTaskDto[]>();
  for (const task of tasks) {
    out.set(
      task.id,
      task.dependsOn
        .map((id) => byId.get(id))
        .filter((d): d is OpsTaskDto => !!d && d.status !== 'done'),
    );
  }
  return out;
}

export interface OpsTasksState {
  tasks: OpsTaskDto[];
  departments: OpsDepartmentDto[];
  staff: OpsStaffSummaryDto[];
  blockers: Map<string, OpsTaskDto[]>;
  loading: boolean;
  loadError: string;
  error: string;
  busyId: string | null;
  create: (input: OpsTaskInput) => Promise<void>;
  /**
   * What must finish before this task can. Replaces the whole set — the endpoint is a PUT.
   *
   * The server checks the entire graph and refuses an edge that would close a cycle, naming the
   * path it found. `run` turns those ids into titles, because a plan nobody can start is a real
   * problem and only the operator can decide which link to cut.
   */
  setDependencies: (task: OpsTaskDto, dependsOn: string[]) => Promise<boolean>;
  setStatus: (task: OpsTaskDto, status: OpsTaskStatus) => Promise<void>;
  update: (task: OpsTaskDto, patch: Partial<OpsTaskInput>) => Promise<void>;
  remove: (task: OpsTaskDto) => Promise<void>;
  reload: () => Promise<void>;
}

export function useOpsTasks(token: string, eventSlug?: string): OpsTasksState {
  const [tasks, setTasks] = useState<OpsTaskDto[] | null>(null);
  const [departments, setDepartments] = useState<OpsDepartmentDto[]>([]);
  const [staff, setStaff] = useState<OpsStaffSummaryDto[]>([]);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [t, d, s] = await Promise.all([
        opsApi.listTasks(token, eventSlug),
        opsApi.listDepartments(token, eventSlug).catch(() => [] as OpsDepartmentDto[]),
        opsApi.listStaff(token, eventSlug).catch(() => [] as OpsStaffSummaryDto[]),
      ]);
      setTasks(t);
      setDepartments(d);
      setStaff(s);
      setLoadError('');
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'โหลดแผนงานไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  /*
   * A refused cycle arrives as a list of task ids, and this hook is the only place that holds the
   * titles — so it is the only place that can say "A → B → C → A is why" rather than "you cannot
   * do that". The classic panel did this by handing `describeIds` to a shared helper; here the
   * data and the explanation already live together, so there is nothing to hand over.
   */
  const explain = useCallback((e: unknown, fallback: string): string => {
    if (!(e instanceof ApiError)) return fallback;
    const body = e.body as { code?: string; path?: unknown } | undefined;
    if (body?.code === 'OPS_TASK_DEPENDENCY_CYCLE' && Array.isArray(body.path)) {
      const names = (body.path as string[])
        .map((id) => (tasks ?? []).find((t) => t.id === id)?.title ?? id);
      return `${e.message} — ${names.join(' → ')}`;
    }
    return e.message;
  }, [tasks]);

  const run = useCallback(async (id: string | null, fn: () => Promise<unknown>, fallback: string) => {
    setBusyId(id);
    setError('');
    try {
      await fn();
      await load();
      return true;
    } catch (e) {
      // A version conflict is the expected failure here, not an exceptional one: somebody else
      // moved the task. It is shown as-is so the person reloads rather than fighting the board.
      setError(explain(e, fallback));
      return false;
    } finally {
      setBusyId(null);
    }
  }, [load, explain]);

  const list = tasks ?? [];

  return {
    tasks: list, departments, staff,
    blockers: useMemo(() => blockersOf(list), [list]),
    loading: tasks === null && !loadError,
    loadError, error, busyId,
    /*
     * `run` reports whether the write went through, which only the dependency editor needs — it
     * closes on success and stays open on a refused cycle so the path is still on screen next to
     * the boxes that caused it. The rest discard it and keep their `Promise<void>` signature.
     */
    create: async (input) => { await run(null, () => opsApi.createTask(token, input, eventSlug), 'สร้างงานไม่สำเร็จ'); },
    setDependencies: (task, dependsOn) => run(
      task.id,
      () => opsApi.setTaskDependencies(token, task.id, dependsOn, eventSlug),
      'บันทึกลำดับก่อนหลังไม่สำเร็จ',
    ),
    setStatus: async (task, status) => {
      await run(task.id, () => opsApi.updateTask(token, task.id, { status, version: task.version }, eventSlug), 'เปลี่ยนสถานะไม่สำเร็จ');
    },
    update: async (task, patch) => {
      await run(task.id, () => opsApi.updateTask(token, task.id, { ...patch, version: task.version }, eventSlug), 'บันทึกไม่สำเร็จ');
    },
    remove: async (task) => {
      await run(task.id, () => opsApi.deleteTask(token, task.id, task.version, eventSlug), 'ลบงานไม่สำเร็จ');
    },
    reload: load,
  };
}
